import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminEmptyState from './AdminEmptyState'
import AdminPagination from './AdminPagination'
import AdminSkeleton from './AdminSkeleton'
import AdminUserDetailDrawer from './AdminUserDetailDrawer'
import { useAdminUi } from './AdminUiProvider'
import type { AdminUserRow } from './adminTypes'
import { exportCsv } from './adminUtils'

type Props = {
  token: string
  initialOpenUserId?: number | null
  onConsumedInitialUser?: () => void
}

type UserFilter = '' | 'active' | 'blocked' | 'sellers' | 'pending_verification'

const USER_FILTERS: UserFilter[] = ['', 'active', 'blocked', 'sellers', 'pending_verification']
const EXPORT_BATCH = 200

export default function AdminUsersPanel({ token, initialOpenUserId = null, onConsumedInitialUser }: Props) {
  const { t } = useTranslation()
  const { toast } = useAdminUi()
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<UserFilter>('')
  const [items, setItems] = useState<AdminUserRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<AdminUserRow | null>(null)
  const pageSize = 50

  const listParams = useCallback(
    () => ({
      q: q.trim() || undefined,
      filter: filter || undefined,
    }),
    [q, filter],
  )

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    adminAPI
      .listUsers(token, { ...listParams(), skip: (page - 1) * pageSize, limit: pageSize })
      .then((res) => {
        setItems((res.data?.items as AdminUserRow[]) ?? [])
        setTotal(Number(res.data?.total ?? 0))
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, listParams, page])

  useEffect(() => {
    const tmr = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [load, q])

  useEffect(() => {
    setPage(1)
  }, [q, filter])

  useEffect(() => {
    if (!initialOpenUserId) return
    adminAPI
      .getUser(token, initialOpenUserId)
      .then((res) => setSelected(res.data as AdminUserRow))
      .finally(() => onConsumedInitialUser?.())
  }, [token, initialOpenUserId, onConsumedInitialUser])

  function handleUserUpdated(updated: AdminUserRow) {
    setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
    setSelected((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev))
  }

  async function fetchAllUsers(): Promise<AdminUserRow[]> {
    const params = listParams()
    const rows: AdminUserRow[] = []
    let skip = 0
    let totalCount = Number.POSITIVE_INFINITY

    while (skip < totalCount) {
      const res = await adminAPI.listUsers(token, { ...params, skip, limit: EXPORT_BATCH })
      const batch = (res.data?.items as AdminUserRow[]) ?? []
      totalCount = Number(res.data?.total ?? batch.length)
      rows.push(...batch)
      if (batch.length === 0) break
      skip += batch.length
    }

    return rows
  }

  async function exportRows() {
    setExporting(true)
    try {
      const rows = await fetchAllUsers()
      exportCsv(
        'users.csv',
        ['id', 'email', 'name', 'role', 'active', 'listings_active', 'listings_sold', 'earnings'],
        rows.map((user) => [
          user.id,
          user.email,
          user.full_name ?? '',
          user.role ?? '',
          user.is_active ? 'yes' : 'no',
          user.listings_active,
          user.listings_sold,
          user.seller_earnings_eur,
        ]),
      )
      toast.success(t('admin.toast.exported', { count: rows.length }))
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : t('common.error')
      toast.error(message || t('common.error'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.users')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.usersIntro')}</p>
        </div>
        <div className="vc-admin-panel__actions">
          <button
            type="button"
            className="vc-admin-btn vc-admin-btn--ghost"
            disabled={exporting}
            onClick={() => void exportRows()}
          >
            {exporting ? '…' : t('admin.exportCsv')}
          </button>
        </div>
      </header>

      <div className="vc-admin-filters">
        {USER_FILTERS.map((key) => (
          <button
            key={key || 'all-users'}
            type="button"
            className={`vc-admin-chip${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(key ? `admin.usersFilterTag.${key}` : 'admin.usersFilterTag.all')}
          </button>
        ))}
      </div>

      <input
        type="search"
        className="vc-admin-input vc-admin-input--search"
        placeholder={t('admin.searchUsers')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {error ? <p className="vc-admin-error">{error}</p> : null}
      {loading && items.length === 0 ? <AdminSkeleton variant="table" rows={6} /> : null}
      {!loading && items.length === 0 ? (
        <AdminEmptyState title={t('admin.noUsers')} hint={t('admin.usersIntro')} />
      ) : null}
      {items.length > 0 ? (
        <>
        <div className="vc-admin-table-wrap">
          <table className="vc-admin-table vc-admin-table--clickable">
            <thead>
              <tr>
                <th>{t('auth.email')}</th>
                <th>{t('admin.role')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('admin.listingsCount')}</th>
                <th>{t('admin.sellerEarnings')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((user) => (
                <tr key={user.id} onClick={() => setSelected(user)}>
                  <td>
                    <strong>{user.full_name || user.email}</strong>
                    {user.full_name ? <div className="vc-admin-muted">{user.email}</div> : null}
                  </td>
                  <td>{user.role}</td>
                  <td>
                    <span className={`vc-admin-badge${user.is_active ? '' : ' vc-admin-badge--warn'}`}>
                      {user.is_active ? t('admin.statusActive') : t('admin.statusBlocked')}
                    </span>
                  </td>
                  <td>
                    {user.listings_active} / {user.listings_sold}
                  </td>
                  <td>€{user.seller_earnings_eur.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
        </>
      ) : null}

      {selected ? (
        <AdminUserDetailDrawer
          token={token}
          user={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUserUpdated}
        />
      ) : null}
    </div>
  )
}
